import type * as GeoJSON from 'geojson'
import type { GhostDto, GhostKind, TurfMemberDto } from '../../types/turf'

// [O4] Pure GeoJSON builders + the drive-path dash animation for the organizer turf map.

export const O4_SOURCE_IDS = { STOPS: 'o4-stops', GHOSTS: 'o4-ghosts', ROUTE_ANIM: 'o4-route-anim' } as const
export const O4_LAYER_IDS = {
  STOP_CIRCLES: 'o4-stop-circles',
  STOP_NUMBERS: 'o4-stop-numbers',
  ADDIN_DIAMONDS: 'o4-addin-diamonds',
  GHOST_SITES: 'o4-ghost-sites',
  GHOST_SITE_PLUS: 'o4-ghost-site-plus',
  GHOST_EXTRAS: 'o4-ghost-extras',
  GHOST_EXTRAS_TEXT: 'o4-ghost-extras-text',
  ROUTE_ANIM: 'o4-route-anim',
} as const
export const ADDIN_DIAMOND_IMAGE = 'addin-diamond'

export interface StopFeatureProps {
  /** 1-based stop number along the walk (co-located members share one). */
  order: number
  /** True when any member at this location came through an added stop. */
  isAddIn: boolean
  /** The first (lowest WalkOrder) add-in OpportunisticStop id at this location (null when none) — for onAddInClick. */
  stopId: string | null
}

export interface GhostFeatureProps {
  key: string
  kind: GhostKind
  count: number
}

/** Group key at 5 decimals, as Optimize and the route group co-located members (O4-8, R8). Location
 *  only: people added at an existing stop (a "+N" extras add, or R7's home add-in at a home stop) share
 *  that stop's number, as they do on the route and the phone. */
const stopKey = (lat: number, lng: number) => `${lat.toFixed(5)},${lng.toFixed(5)}`

/** One numbered point per stop location, in WalkOrder. Unplaced members (no stop point) are dropped. */
export function buildStopFeatures(
  members: TurfMemberDto[],
): GeoJSON.FeatureCollection<GeoJSON.Point, StopFeatureProps> {
  const groups = new Map<string, {
    walkOrder: number; lat: number; lng: number; stopId: string | null; stopWalkOrder: number
  }>()
  for (const m of members) {
    if (m.stopLatitude == null || m.stopLongitude == null) continue
    const key = stopKey(m.stopLatitude, m.stopLongitude)
    const g = groups.get(key) ?? {
      walkOrder: m.walkOrder, lat: m.stopLatitude, lng: m.stopLongitude, stopId: null, stopWalkOrder: Infinity,
    }
    if (m.walkOrder < g.walkOrder) g.walkOrder = m.walkOrder
    if (m.opportunisticStopId && m.walkOrder < g.stopWalkOrder) {
      g.stopId = m.opportunisticStopId
      g.stopWalkOrder = m.walkOrder
    }
    groups.set(key, g)
  }
  const ordered = [...groups.values()].sort((a, b) => a.walkOrder - b.walkOrder)
  return {
    type: 'FeatureCollection',
    features: ordered.map((g, i) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [g.lng, g.lat] },
      properties: { order: i + 1, isAddIn: g.stopId != null, stopId: g.stopId },
    })),
  }
}

/** A straight line through the stops in order, for a turf with no stored route. */
export function buildFallbackPath(
  stops: GeoJSON.FeatureCollection<GeoJSON.Point, { order: number }>,
): GeoJSON.Feature<GeoJSON.LineString> | null {
  if (stops.features.length < 2) return null
  const coordinates = [...stops.features]
    .sort((a, b) => a.properties.order - b.properties.order)
    .map(f => f.geometry.coordinates)
  return { type: 'Feature', geometry: { type: 'LineString', coordinates }, properties: {} }
}

/** Suggested stops from the corridor search; `count` = the people the suggestion carries. */
export function buildGhostFeatures(
  ghosts: GhostDto[],
): GeoJSON.FeatureCollection<GeoJSON.Point, GhostFeatureProps> {
  return {
    type: 'FeatureCollection',
    features: ghosts.map(g => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [g.longitude, g.latitude] },
      properties: { key: g.key, kind: g.kind, count: g.people.length },
    })),
  }
}

export interface O4ClickPoint { x: number; y: number }
export interface O4ClickHandlers {
  onGhostClick?: (key: string, point?: O4ClickPoint) => void
  onAddInClick?: (stopId: string, point?: O4ClickPoint) => void
}

/** Routes a map click on an O4 layer (ghost circle / "+N" badge / add-in diamond) to its handler.
 *  Returns true when the click belongs to O4 and is consumed; false hands it to the other layers.
 *  A layer whose handler is absent is not interactive, so it is left to the caller. */
export function routeO4Click(
  layerId: string,
  properties: Record<string, unknown> | null | undefined,
  point: O4ClickPoint | undefined,
  { onGhostClick, onAddInClick }: O4ClickHandlers,
): boolean {
  if ((layerId === O4_LAYER_IDS.GHOST_SITES || layerId === O4_LAYER_IDS.GHOST_EXTRAS) && onGhostClick) {
    const key = properties?.key
    if (typeof key === 'string' && key) onGhostClick(key, point)
    return true
  }
  if (layerId === O4_LAYER_IDS.ADDIN_DIAMONDS && onAddInClick) {
    const stopId = properties?.stopId
    if (typeof stopId === 'string' && stopId) onAddInClick(stopId, point)
    return true
  }
  return false
}

interface ImageMap {
  hasImage(id: string): boolean
  addImage(id: string, image: { width: number; height: number; data: Uint8Array | Uint8ClampedArray }): void
}

/** Draws the add-in diamond once onto a canvas and registers it as a map image. A no-op when the
 *  image is already registered (style reload re-requests it via styleimagemissing) or when there
 *  is no 2D canvas (jsdom). */
export function registerAddInDiamond(map: ImageMap, fill: string, stroke: string): void {
  if (map.hasImage(ADDIN_DIAMOND_IMAGE)) return
  const size = 28
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const mid = size / 2
  const r = mid - 2
  ctx.beginPath()
  ctx.moveTo(mid, mid - r)
  ctx.lineTo(mid + r, mid)
  ctx.lineTo(mid, mid + r)
  ctx.lineTo(mid - r, mid)
  ctx.closePath()
  ctx.fillStyle = fill
  ctx.fill()
  ctx.lineWidth = 2
  ctx.strokeStyle = stroke
  ctx.stroke()
  const img = ctx.getImageData(0, 0, size, size)
  map.addImage(ADDIN_DIAMOND_IMAGE, { width: size, height: size, data: new Uint8ClampedArray(img.data) })
}

/** "Marching" dash steps (the MapLibre animated-line pattern), one cycle per PATH_CYCLE_MS. */
export const PATH_DASH_SEQUENCE: number[][] = [
  [0, 4, 3], [0.5, 4, 2.5], [1, 4, 2], [1.5, 4, 1.5], [2, 4, 1], [2.5, 4, 0.5], [3, 4, 0],
  [0, 0.5, 3, 3.5], [0, 1, 3, 3], [0, 1.5, 3, 2.5], [0, 2, 3, 2], [0, 2.5, 3, 1.5], [0, 3, 3, 1], [0, 3.5, 3, 0.5],
]
export const PATH_CYCLE_MS = 1600

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

interface PaintableMap {
  getLayer(id: string): unknown
  setPaintProperty(layerId: string, name: string, value: unknown): void
}

/**
 * Animates `layerId`'s line-dasharray on requestAnimationFrame by writing the paint property on
 * the map directly — no React state, so no re-render per frame. Returns the cancel function.
 * Under prefers-reduced-motion it never starts.
 */
export function startDashAnimation(
  map: PaintableMap,
  layerId: string,
  opts: {
    raf?: (cb: (t: number) => void) => number
    caf?: (handle: number) => void
    reducedMotion?: boolean
  } = {},
): () => void {
  const raf = opts.raf ?? ((cb: (t: number) => void) => window.requestAnimationFrame(cb))
  const caf = opts.caf ?? ((h: number) => window.cancelAnimationFrame(h))
  if (opts.reducedMotion ?? prefersReducedMotion()) return () => {}

  const stepMs = PATH_CYCLE_MS / PATH_DASH_SEQUENCE.length
  let handle = 0
  let lastStep = -1
  let stopped = false
  const frame = (t: number) => {
    if (stopped) return
    const step = Math.floor((t % PATH_CYCLE_MS) / stepMs)
    if (step !== lastStep && map.getLayer(layerId)) {
      map.setPaintProperty(layerId, 'line-dasharray', PATH_DASH_SEQUENCE[step])
      lastStep = step
    }
    handle = raf(frame)
  }
  handle = raf(frame)
  return () => {
    stopped = true
    caf(handle)
  }
}
